import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { handleError } from '../../../../../shared/ErrorCatching';
import { useTelemetryContext } from '../../../../../telemetry/telemetryContext';
import { getUserInfo } from '../../../../../telemetry/utils';
import { Translate } from '../../../../../shared/LanguageService';
import * as Actions from '../Actions';
import * as OrderState from '../OrderState';
import { IBasicOrderInfo, OrderTypes } from '../Types';

type UseOrderStateUpdateProps = {
    orderId: number;
    order: IBasicOrderInfo;
    productProperties: any[];
    user: any;
    showSuccess: (text: string) => void;
};

export function useOrderStateUpdate(props: UseOrderStateUpdateProps) {
    const { orderId, order, productProperties, user, showSuccess } = props;
    const dispatch = useDispatch<any>();
    const { logger } = useTelemetryContext();

    const [showUpdateOrderStateConfirmation, setShowUpdateOrderStateConfirmation] =
        useState(false);
    const [orderStateToConfirm, setOrderStateToConfirm] = useState<number>(null);
    const [removeOrderStateTitle, setRemoveOrderStateTitle] = useState<string>(undefined);
    const [removeOrderStateText, setRemoveOrderStateText] = useState<string>(undefined);

    const updateOrderState = (orderState: number): void => {
        dispatch(Actions.updateOrderState(orderId, orderState))
            .then(() => {
                switch (orderState) {
                    case OrderState.Normal:
                        showSuccess(
                            Translate(
                                'view-order/basic-order-info/orderstate/update/normal/success'
                            )
                        );
                        break;
                    case OrderState.Removed:
                        showSuccess(
                            Translate(
                                'view-order/basic-order-info/orderstate/update/removed/success'
                            )
                        );
                        break;
                    case OrderState.BlockedForBooking:
                        showSuccess(
                            Translate(
                                'view-order/basic-order-info/orderstate/update/blocked-for-booking/success'
                            )
                        );
                        break;
                    case OrderState.BlockedForInvoice:
                        showSuccess(
                            Translate(
                                'view-order/basic-order-info/orderstate/update/blocked-for-invoice/success'
                            )
                        );
                        break;
                    default:
                        break;
                }
            })
            .catch((error: any) => handleError(error))
            .then(() => {
                setShowUpdateOrderStateConfirmation(false);
                setOrderStateToConfirm(null);
            });
    };

    const updateOrderStateConfirmation = (orderState: number): void => {
        if (orderState === OrderState.Removed) {
            if (
                (order.orderType & OrderTypes.photo) !== 0 &&
                productProperties.find((x) => x.value === 27 /* Measured */)
            ) {
                setShowUpdateOrderStateConfirmation(true);
                setOrderStateToConfirm(orderState);
                setRemoveOrderStateTitle(
                    'view-order/basic-order-info/orderstate/removed/photo/confirmation/title'
                );
                setRemoveOrderStateText(
                    'view-order/basic-order-info/orderstate/removed/photo/confirmation/text'
                );
            } else if ((order.orderType & OrderTypes.plan) !== 0) {
                dispatch(Actions.canRemovePlanOrder(orderId))
                    .then((canRemoveWithoutConfirmation: boolean) => {
                        if (!canRemoveWithoutConfirmation) {
                            setShowUpdateOrderStateConfirmation(true);
                            setOrderStateToConfirm(orderState);
                            setRemoveOrderStateTitle(
                                'view-order/basic-order-info/orderstate/removed/plan/confirmation/title'
                            );
                            setRemoveOrderStateText(
                                'view-order/basic-order-info/orderstate/removed/plan/confirmation/text'
                            );
                        } else {
                            updateOrderState(orderState);
                        }
                    })
                    .catch((error: any) => {
                        logger.logException(error, {
                            origin: 'BasicOrderInfoPage.OrderStateUpdate.canRemovePlanOrder',
                            userInfo: getUserInfo(user)
                        });
                    });
            } else {
                updateOrderState(orderState);
            }
        } else {
            updateOrderState(orderState);
        }
    };

    const closeUpdateOrderStateConfirmation = (): void => {
        setShowUpdateOrderStateConfirmation(false);
    };

    return {
        showUpdateOrderStateConfirmation,
        orderStateToConfirm,
        removeOrderStateTitle,
        removeOrderStateText,
        updateOrderState,
        updateOrderStateConfirmation,
        closeUpdateOrderStateConfirmation
    };
}
